/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/CalorificValues/AdminCalorificValues.tsx.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../../../components/common/Button";
import OutlineButton from "../../../../components/common/OutlineButton";
import {
  calorificValuesAdminService,
  type CalorificValueAdmin,
} from "../../../../services/calorificValuesAdminService";
import { useFuelMixDropdownOptions } from "../shared/useFuelMixDropdownOptions";
import "../Admin.css";
import "../CharacterizationFactors/AdminCharacterizationFactors.css";

const emptyForm = () => ({
  fuelMix: "",
  unit: "",
  calorificValue: "",
  sortOrder: "0",
  isActive: true,
});

function normalizeCalorificPayload(trimmed: string): string | null {
  return trimmed === "" ? null : trimmed;
}

const AdminCalorificValues: React.FC = () => {
  const [rows, setRows] = useState<CalorificValueAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);

  const [deleteTarget, setDeleteTarget] = useState<CalorificValueAdmin | null>(null);

  const { options: fuelMixOptions } = useFuelMixDropdownOptions();

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await calorificValuesAdminService.list();
      setRows(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load calorific values");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const o = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        return o !== 0 ? o : a.id - b.id;
      }),
    [rows]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (r: CalorificValueAdmin) => {
    setEditingId(r.id);
    setForm({
      fuelMix: r.fuelMix ?? "",
      unit: r.unit ?? "",
      calorificValue: r.calorificValue ?? "",
      sortOrder: String(r.sortOrder ?? 0),
      isActive: Boolean(r.isActive),
    });
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setError(null);
  };

  const handleSave = async () => {
    const fuelMix = form.fuelMix.trim();
    const unit = form.unit.trim();
    if (!fuelMix || !unit) {
      setError("Fuel mix and unit are required.");
      return;
    }

    const sortOrder = Number.parseInt(String(form.sortOrder).trim(), 10);
    const sortOrderNum = Number.isFinite(sortOrder) ? sortOrder : 0;
    const calorificValue = normalizeCalorificPayload(form.calorificValue.trim());

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingId == null) {
        await calorificValuesAdminService.create({
          fuelMix,
          unit,
          calorificValue,
          sortOrder: sortOrderNum,
          isActive: form.isActive,
        });
        setSuccess("Calorific value created.");
      } else {
        await calorificValuesAdminService.update(editingId, {
          fuelMix,
          unit,
          calorificValue,
          sortOrder: sortOrderNum,
          isActive: form.isActive,
        });
        setSuccess("Calorific value updated.");
      }
      closeModal();
      await load();
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setError(null);
    try {
      await calorificValuesAdminService.delete(deleteTarget.id);
      setSuccess("Deleted.");
      setDeleteTarget(null);
      await load();
      setTimeout(() => setSuccess(null), 4000);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const displayValue = (v: string | null) =>
    v == null || v === "" ? <span className="muted">N/A</span> : v;

  return (
    <div className="background-form-container analysis-shell admin-char-factors">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Calorific values</span>
      </div>

      <div className="admin-form-root">
        <div className="dataset-container">
          <div className="dataset-section">
            <h3 className="dataset-section-title">Manage calorific values</h3>
            <p className="muted" style={{ marginBottom: "1rem" }}>
              Fuel mix calorific data for LCA calculations. Leave calorific value empty for N/A
              (e.g. Electricity). Inactive rows are hidden from the public list (GET /calorific-values).
              Admin list includes all rows.
            </p>

            {error && !modalOpen && (
              <div className="error-message" style={{ marginBottom: 12 }}>
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="success-message" style={{ marginBottom: 12 }}>
                <span className="success-icon">✅</span>
                <span>{success}</span>
              </div>
            )}

            <div className="char-factors-toolbar">
              <Button label="Add row" type="button" onClick={openCreate} disabled={loading} />
            </div>

            {loading ? (
              <p>Loading…</p>
            ) : sortedRows.length === 0 ? (
              <p className="muted">No rows yet. Click &quot;Add row&quot; to create one.</p>
            ) : (
              <div className="char-factors-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Fuel mix</th>
                      <th>Unit</th>
                      <th>Calorific value</th>
                      <th>Status</th>
                      <th>Updated</th>
                      <th className="cell-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((r) => (
                      <tr key={r.id}>
                        <td>{r.sortOrder}</td>
                        <td>{r.fuelMix}</td>
                        <td>{r.unit}</td>
                        <td>{displayValue(r.calorificValue)}</td>
                        <td>
                          {r.isActive ? (
                            <span>Active</span>
                          ) : (
                            <span className="badge-inactive">Inactive</span>
                          )}
                        </td>
                        <td className="muted" style={{ fontSize: "0.8rem" }}>
                          {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : "—"}
                        </td>
                        <td className="cell-actions">
                          <OutlineButton label="Edit" type="button" onClick={() => openEdit(r)} />
                          <OutlineButton
                            label="Delete"
                            type="button"
                            onClick={() => {
                              setError(null);
                              setDeleteTarget(r);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="modal-panel" role="dialog" aria-labelledby="cv-modal-title">
            <div className="modal-header" id="cv-modal-title">
              {editingId == null ? "New calorific value" : "Edit calorific value"}
            </div>
            <div className="modal-body">
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}
              <div>
                <label htmlFor="cv-fuelMix">Fuel mix *</label>
                <select
                  id="cv-fuelMix"
                  value={
                    fuelMixOptions.some((o) => o.label === form.fuelMix)
                      ? form.fuelMix
                      : ""
                  }
                  onChange={(e) => setForm((f) => ({ ...f, fuelMix: e.target.value }))}
                  disabled={saving}
                >
                  <option value="">Select a fuel mix…</option>
                  {fuelMixOptions.map((o) => (
                    <option key={o.id} value={o.label}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {fuelMixOptions.length === 0 && (
                  <p className="muted">
                    No “Fuel Mix” dropdown options found. Add options in
                    “Manage dropdowns”.
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="cv-unit">Unit *</label>
                <input
                  id="cv-unit"
                  type="text"
                  value={form.unit}
                  onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
                  disabled={saving}
                  placeholder="e.g. L, m³, kWh"
                />
              </div>
              <div>
                <label htmlFor="cv-value">Calorific value (display)</label>
                <input
                  id="cv-value"
                  type="text"
                  value={form.calorificValue}
                  onChange={(e) => setForm((f) => ({ ...f, calorificValue: e.target.value }))}
                  disabled={saving}
                  placeholder='Optional; e.g. 44.3 MJ/ kg (a). Leave empty for N/A.'
                />
                <p className="muted">Empty is stored as null (N/A).</p>
              </div>
              <div>
                <label htmlFor="cv-sort">Sort order</label>
                <input
                  id="cv-sort"
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                  disabled={saving}
                />
                <p className="muted">Lower numbers appear first in public lists.</p>
              </div>
              <div className="checkbox-row">
                <input
                  id="cv-active"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  disabled={saving}
                />
                <label htmlFor="cv-active">Active (visible on public API)</label>
              </div>
            </div>
            <div className="modal-footer">
              <OutlineButton label="Cancel" type="button" onClick={closeModal} disabled={saving} />
              <Button
                label={saving ? "Saving…" : "Save"}
                type="button"
                onClick={handleSave}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) setDeleteTarget(null);
          }}
        >
          <div className="modal-panel" role="alertdialog" aria-labelledby="cv-del-title">
            <div className="modal-header" id="cv-del-title">
              Delete calorific value?
            </div>
            <div className="modal-body">
              <p>
                Remove <strong>{deleteTarget.fuelMix}</strong>
                {deleteTarget.calorificValue ? (
                  <>
                    {" "}
                    — <span>{deleteTarget.calorificValue}</span>
                  </>
                ) : (
                  <span className="muted"> (N/A)</span>
                )}
                ?
              </p>
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <OutlineButton
                label="Cancel"
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={saving}
              />
              <Button
                label={saving ? "Deleting…" : "Delete"}
                type="button"
                onClick={confirmDelete}
                disabled={saving}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** React component — Admin Calorific Values. */
export default AdminCalorificValues;
